import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Lock, Check, X } from 'lucide-react';
import api from '../lib/api';

/**
 * CommunityLockedSheet — Q34.
 *
 * Bottom-sheet modal shown when a non-eligible user taps the locked
 * "Create community" CTA. Renders the eligibility checklist returned by
 * GET /api/communities/eligibility so the user knows EXACTLY what's
 * missing instead of seeing a vague "not allowed".
 *
 * Portal-rendered so ancestor `transform`/`filter` rules cannot break the
 * `position: fixed` anchoring (iter61 caught the sheet falling below the
 * BottomNav because content-area uses transforms).
 */
export default function CommunityLockedSheet({ open, onClose }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get('/communities/eligibility').then(r => setData(r.data)).catch(() => setData(null));
  }, [open]);

  if (!open) return null;
  const reqs = data?.requirements || [];
  const eligible = data?.eligible;

  return createPortal(
    <div
      className="fixed inset-0 z-[10010] flex items-end justify-center bg-black/50 backdrop-blur-[2px] animate-fade-in"
      onClick={onClose}
      data-testid="community-locked-backdrop"
    >
      <div
        className="w-full max-w-[440px] bg-white rounded-t-3xl shadow-2xl p-6 pb-8 animate-slide-up"
        onClick={e => e.stopPropagation()}
        data-testid="community-locked-sheet"
      >
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-full bg-[#F1F0EA] text-[#3F5200] flex items-center justify-center">
              <Lock size={16} strokeWidth={2.6} />
            </div>
            <div>
              <h3 className="text-base font-extrabold text-[#111] leading-tight">
                Unlock Community Leader
              </h3>
              <p className="text-[11px] text-[#6B6E73]">
                Create and lead your own community on ASAP.
              </p>
            </div>
          </div>
          <button
            data-testid="community-locked-close"
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-[#F1F0EA] flex items-center justify-center"
          >
            <X size={16} />
          </button>
        </div>

        <p className="text-xs text-[#6B6E73] mb-3 leading-relaxed">
          To keep ASAP communities premium, creators need a track record.
        </p>

        <div data-testid="community-locked-checklist" className="space-y-2 mb-5">
          {reqs.map(r => (
            <div
              key={r.key}
              data-testid={`req-${r.key}-${r.met ? 'met' : 'unmet'}`}
              className={`flex items-center gap-3 p-3 rounded-xl border ${
                r.met ? 'bg-[#B6E600]/10 border-[#B6E600]/30' : 'bg-[#FAFAF7] border-black/[0.06]'
              }`}
            >
              <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                r.met ? 'bg-[#B6E600] text-[#111]' : 'bg-white border border-black/[0.08] text-[#9B9EA3]'
              }`}>
                {r.met ? <Check size={12} strokeWidth={3} /> : <Lock size={11} strokeWidth={2.8} />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-[#111]">{r.label}</p>
                {!r.met && (
                  <p className="text-[11px] text-[#6B6E73] mt-0.5">
                    Currently: <span className="font-bold">{String(r.current)}</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>

        {eligible ? (
          <button
            data-testid="community-locked-cta-create"
            onClick={onClose}
            className="w-full bg-[#B6E600] text-[#111] font-bold rounded-full text-sm active:scale-[0.98]"
            style={{ minHeight: 50 }}
          >
            You&rsquo;re eligible — close this and create
          </button>
        ) : (
          <button
            data-testid="community-locked-cta-keep-going"
            onClick={onClose}
            className="w-full bg-[#111] text-white font-bold rounded-full text-sm active:scale-[0.98]"
            style={{ minHeight: 50 }}
          >
            Keep hosting · keep going
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}
